import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { clearCodeFromUrl, readCodeFromUrl } from './joinLink';
import type {
  ClientMessage,
  ErrorCode,
  GameMode,
  Choice,
  PublicPlayer,
  RoomState,
  ServerMessage,
} from '@majority-report/shared';

const SESSION_KEY = 'mr.session';
const MAX_BACKOFF_MS = 8000;

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

interface StoredSession {
  code: string;
  playerId: string;
  token: string;
}

interface GameActions {
  createLobby: (nickname: string, avatar: string) => void;
  joinLobby: (code: string, nickname: string, avatar: string) => void;
  setMode: (mode: GameMode) => void;
  setHearts: (hearts: number) => void;
  setCustomQuestions: (enabled: boolean) => void;
  startGame: () => void;
  submitQuestion: (prompt: string, options: string[]) => void;
  answer: (choice: Choice) => void;
  nextRound: () => void;
  playAgain: () => void;
  leave: () => void;
  /** Discard a room code that came in from a share link, without joining it. */
  dismissInvite: () => void;
}

interface GameContextValue {
  status: ConnectionStatus;
  state: RoomState | null;
  playerId: string | null;
  me: PublicPlayer | null;
  isHost: boolean;
  error: ErrorCode | null;
  clearError: () => void;
  /**
   * A code from a share link that has not been acted on yet. Lives here rather
   * than in the screen that renders it so the top bar can offer a way out of
   * the invite, and dismissing it is visible to both.
   */
  pendingCode: string | null;
  actions: GameActions;
}

const GameContext = createContext<GameContextValue | null>(null);

function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (!parsed.code || !parsed.playerId || !parsed.token) return null;
    return { code: parsed.code, playerId: parsed.playerId, token: parsed.token };
  } catch {
    return null;
  }
}

/**
 * The session to start from.
 *
 * A room code in the URL wins over a stored session for a *different* room:
 * tapping a share link means going there, and silently resuming the old seat
 * would ignore the link entirely. For the *same* room the stored session is
 * kept, so they reclaim their seat instead of trying to join twice.
 */
function initialSession(): StoredSession | null {
  const stored = loadSession();
  const urlCode = readCodeFromUrl();
  if (stored && urlCode && stored.code !== urlCode) {
    saveSession(null);
    return null;
  }
  return stored;
}

function saveSession(session: StoredSession | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* private browsing — the game still works, it just cannot resume. */
  }
}

function socketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // Same-origin: Vite proxies /ws in dev, express serves it in production.
  return `${protocol}//${window.location.host}/ws`;
}

/**
 * Owns the single WebSocket for the whole app.
 *
 * The server is the only source of truth: every action is fire-and-forget and
 * the UI only ever renders the last `state` snapshot it was handed. That means
 * no optimistic updates to reconcile and no divergence between players.
 */
export function GameProvider({ children }: { children: ReactNode }) {
  const socketRef = useRef<WebSocket | null>(null);
  const queueRef = useRef<ClientMessage[]>([]);
  const sessionRef = useRef<StoredSession | null>(initialSession());
  const attemptRef = useRef(0);
  const reconnectRef = useRef<number | null>(null);
  /** Set while we deliberately tear the socket down, to suppress auto-reconnect. */
  const intentionalCloseRef = useRef(false);
  const mountedRef = useRef(true);

  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [state, setState] = useState<RoomState | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(sessionRef.current?.playerId ?? null);
  const [error, setError] = useState<ErrorCode | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(readCodeFromUrl);

  const flushQueue = useCallback(() => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return;
    const pending = queueRef.current;
    queueRef.current = [];
    for (const message of pending) socket.send(JSON.stringify(message));
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (socketRef.current && socketRef.current.readyState <= WebSocket.OPEN) return;

    setStatus('connecting');
    const socket = new WebSocket(socketUrl());
    socketRef.current = socket;

    socket.onopen = () => {
      if (!mountedRef.current) return;
      attemptRef.current = 0;
      setStatus('open');

      // Reclaim our seat before anything queued, so the server knows who we are.
      const session = sessionRef.current;
      if (session) {
        socket.send(
          JSON.stringify({
            type: 'resume',
            code: session.code,
            playerId: session.playerId,
            token: session.token,
          } satisfies ClientMessage),
        );
      }
      flushQueue();
    };

    socket.onmessage = (event) => {
      if (!mountedRef.current) return;
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }

      switch (message.type) {
        case 'welcome': {
          const session: StoredSession = {
            code: message.code,
            playerId: message.playerId,
            token: message.token,
          };
          sessionRef.current = session;
          saveSession(session);
          // The code has done its job; keep it out of history.
          clearCodeFromUrl();
          setPendingCode(null);
          setPlayerId(message.playerId);
          setState(message.state);
          setError(null);
          break;
        }
        case 'state':
          setState(message.state);
          break;
        case 'error':
          setError(message.code);
          break;
        case 'kicked':
          // Our seat is gone for good — wipe it so we do not keep resuming into nothing.
          sessionRef.current = null;
          saveSession(null);
          setPlayerId(null);
          setState(null);
          // A missing room on first load is just a stale tab, not worth a scary banner.
          setError(message.code === 'session_expired' ? null : message.code);
          break;
        case 'pong':
          break;
      }
    };

    socket.onclose = () => {
      if (socketRef.current === socket) socketRef.current = null;
      if (!mountedRef.current) return;
      setStatus('closed');

      if (intentionalCloseRef.current) {
        intentionalCloseRef.current = false;
        return;
      }
      // Exponential backoff, capped — a reload storm helps nobody.
      const delay = Math.min(500 * 2 ** attemptRef.current, MAX_BACKOFF_MS);
      attemptRef.current += 1;
      reconnectRef.current = window.setTimeout(connect, delay);
    };

    socket.onerror = () => socket.close();
  }, [flushQueue]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectRef.current !== null) window.clearTimeout(reconnectRef.current);
      intentionalCloseRef.current = true;
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [connect]);

  const send = useCallback(
    (message: ClientMessage) => {
      const socket = socketRef.current;
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
      else {
        queueRef.current.push(message);
        connect();
      }
    },
    [connect],
  );

  const actions = useMemo<GameActions>(
    () => ({
      createLobby: (nickname, avatar) => {
        // Drop any old seat first so the reconnect logic cannot drag us back into it.
        sessionRef.current = null;
        saveSession(null);
        send({ type: 'create_lobby', nickname, avatar });
      },
      joinLobby: (code, nickname, avatar) => {
        sessionRef.current = null;
        saveSession(null);
        send({ type: 'join_lobby', code: code.toUpperCase(), nickname, avatar });
      },
      setMode: (mode) => send({ type: 'set_mode', mode }),
      setHearts: (hearts) => send({ type: 'set_hearts', hearts }),
      setCustomQuestions: (enabled) => send({ type: 'set_custom_questions', enabled }),
      startGame: () => send({ type: 'start_game' }),
      submitQuestion: (prompt, options) => send({ type: 'submit_question', prompt, options }),
      answer: (choice) => send({ type: 'answer', choice }),
      nextRound: () => send({ type: 'next_round' }),
      playAgain: () => send({ type: 'play_again' }),
      dismissInvite: () => {
        clearCodeFromUrl();
        setPendingCode(null);
      },
      leave: () => {
        send({ type: 'leave' });
        sessionRef.current = null;
        saveSession(null);
        setState(null);
        setPlayerId(null);
      },
    }),
    [send],
  );

  const me = useMemo(
    () => state?.players.find((p) => p.id === playerId) ?? null,
    [state, playerId],
  );

  const value = useMemo<GameContextValue>(
    () => ({
      status,
      state,
      playerId,
      me,
      isHost: Boolean(playerId) && state?.hostId === playerId,
      error,
      clearError: () => setError(null),
      pendingCode,
      actions,
    }),
    [status, state, playerId, me, error, pendingCode, actions],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const context = useContext(GameContext);
  if (!context) throw new Error('useGame must be used inside a <GameProvider>');
  return context;
}
